import React from "react";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, Button, Box, Heading, Spinner } from "@chakra-ui/react";
import _ from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import shallow from "zustand/shallow";

import Api from "@/js/api/authorisedApi";
import ClassroomEditSession from "@/js/components/ClassroomEditSession";
import ClassroomStartSession from "@/js/components/ClassroomStartSession";
import FullPageLoader from "@/js/components/FullPageLoader";
import GoogleSyncErrorModal from "@/js/components/GoogleSyncErrorModal";
import GoogleSyncModal from "@/js/components/GoogleSyncModal";
import GoogleSyncSuccessModal from "@/js/components/GoogleSyncSuccessModal";
import { withRouter } from "@/js/components/Root/withRouter";
import { classroomActiveInfoV2 } from "@/js/utils/classroomSessionUtils";
import eventService from "@/js/services/eventService";

import useStore from "@/stores/index";
import { getFeatures, useClasswizeConfigStore } from "@/stores/classwizeConfigStore";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import ClassroomTileV2 from "@/js/components/ClassroomTileV2";
import Nav from "@/js/components/Nav";
import { CLASSROOM_TYPES } from "@/types/classrooms";
import { CtxBarRight } from "./CtxBarRight";
import { ChakraFAIcon } from "@/components/ui/Icon";

class Classrooms extends React.Component {
	constructor(props) {
		const store = useStore.getState();
		super(props);
		this.state = {
			/**
			 * Only show the "We're loading your classes as fast as we can." message if the initial load is not complete.
			 * This is to prevent the message from flashing when the classrooms are refetched upon receiving a CLASS_STARTED event.
			 */
			initialLoadComplete: false,
			features: getFeatures(),

			isMonitoringAuthorised: !!_.intersection(
				useClasswizeConfigStore.getState().data.monitor_groups || [],
				useUserSessionStore.getState().getUsersGroups()
			).length,

			googleSyncStatus: {},
			googleSyncStats: {},
			googleSyncInitiated: false,
			openGoogleSyncModal: false,
			isGoogleSyncModalCloseable: true,
			openGoogleSyncSuccessModal: false,

			openGoogleSyncErrorModal: false,
			googleSyncError: "",

			showStartClassModal: false,
			showEditClassModal: false,
			showMonitoringClassActiveWarning: false,
			currentMonitoring: {},
			startClassSessionError: false,
			startClassSessionIsMonitoring: false,
			selectedClassroomName: null,
			classExpiryStr: null,
			showUnpinnedClasses: store.classroom.showUnpinnedClasses,
			featureToggles: useStore.getState().featureToggles,
		};
		this.initialGoogleSyncStatusFetched = false;
		this.googleSyncStateInterval = null;
		this.closeGoogleSyncModalTimeout = null;

		this.unsubClasswizeConfig = () => {};
		this.unsubChatWindowOpenStatus = () => {};
		this.unsubChatWindowMinimized = () => {};
		this.unsubClassroomStore = () => {};
		this.unsubShowUnpinnedClasses = () => {};
		this.unsubClassroomSessionStore = () => {};

		this.navRef = React.createRef();

		this.unsubUserSessionStore = () => {};
		this.unsubEventServiceStatus = () => {};
	}

	handleClassStartedEvent = () => {
		useStore.getState().classroom.fetchClassrooms();
	};

	addEventServiceListeners = () => {
		if (eventService.eventSource) {
			eventService.eventSource.addEventListener("CLASS_STARTED", this.handleClassStartedEvent);
		}
	};

	removeEventServiceListeners = () => {
		if (eventService.eventSource) {
			eventService.eventSource.removeEventListener("CLASS_STARTED", this.handleClassStartedEvent);
		}
	};

	componentDidMount() {
		useStore.getState().classroom.fetchClassrooms();

		this.unsubUserSessionStore = useUserSessionStore.subscribe(this.onUserSessionChanged);

		this.unsubClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(state) => {
				this.onChangeClassroomSessionStore(state);
			}
		);

		this.unsubClassroomStore = useStore.subscribe(
			(state) => [state.classroom.loaded, state.classroom.dataV2],
			() => {
				if (this.state.initialLoadComplete) {
					return;
				}
				this.setState({
					initialLoadComplete: useStore.getState().classroom.loaded && !useStore.getState().classroom.fetchInProgress,
				});
			}
		);

		this.unsubShowUnpinnedClasses = useStore.subscribe(
			(state) => [state.classroom.showUnpinnedClasses],

			() =>
				this.setState({
					showUnpinnedClasses: useStore.getState().classroom.showUnpinnedClasses,
				}),
			shallow
		);

		this.unsubChatWindowOpenStatus = useStore.subscribe(
			(state) => [state.chat.chatWindowOpen],

			() =>
				this.setState({
					isChatWindowOpen: useStore.getState().chat.chatWindowOpen,
				}),
			shallow
		);

		this.unsubChatWindowMinimized = useStore.subscribe(
			(state) => [state.chat.chatWindowMinimized],

			() =>
				this.setState({
					isChatWindowMinimized: useStore.getState().chat.chatWindowMinimized,
				})
		);

		setTimeout(() => {
			useClassroomSessionStore.getState().fetchAndPopulateMaxEndTime();
		}, 0);

		this.addEventServiceListeners();

		// Subscribe to event service status changes to re-add listeners when service becomes available
		this.unsubEventServiceStatus = useStore.subscribe(
			(state) => state.eventServiceStatus.status,
			() => {
				this.addEventServiceListeners();
			}
		);
	}

	componentWillUnmount() {
		this.unsubUserSessionStore();

		clearInterval(this.googleSyncStateInterval);
		clearTimeout(this.closeGoogleSyncModalTimeout);

		this.unsubClasswizeConfig();
		this.unsubChatWindowOpenStatus();
		this.unsubChatWindowMinimized();
		this.unsubClassroomStore();
		this.unsubClassroomSessionStore();

		this.removeEventServiceListeners();
		this.unsubEventServiceStatus();
	}

	onChangeClassroomSessionStore = (state) => {
		this.setState({
			showStartClassModal: state.showStartClassModal,
			showEditClassModal: state.showEditClassModal,
			startClassSessionError: state.startClassSessionError,
			startClassSessionIsMonitoring: state.selectedClassroomMonitoring,
			selectedClassroomName: state.selectedClassroomName,
			classExpiryStr: state.classExpiryStr,
			showMonitoringClassActiveWarning: state.showMonitoringClassActiveWarning,
			currentMonitoring: state.currentMonitoring,
		});
	};

	onChange = () => {
		if (useStore.getState().classroom.teacherClassroomsSyncEnabled && !this.initialGoogleSyncStatusFetched) {
			this.initialGoogleSyncStatusFetched = true;
			Api.get(
				"/backend/sync/google/status",
				(data) => {
					this.setState({ googleSyncStatus: data.status });
					if (data.status.status === "running") {
						this.pollGoogleSyncStatus();
					} else if (data.status.status === "error") {
						this.setState({
							googleSyncError: data.status.error_message,
							googleSyncStats: this.makeGoogleSyncStats(data.status),
						});
					} else if (data.status.status === "finished") {
						this.setState({
							googleSyncStats: this.makeGoogleSyncStats(data.status),
						});
					}
				},
				() => {
					this.initialGoogleSyncStatusFetched = false;
				}
			);
		}

		if (this.state.initialLoadComplete) {
			return;
		}

		if (useStore.getState().classroom.loaded && useUserSessionStore.getState().hasLoaded()) {
			this.setState({
				initialLoadComplete: true,
			});
		} else {
			this.setState({
				initialLoadComplete: false,
			});
		}
	};

	onUserSessionChanged = () => {
		this.setState({
			isMonitoringAuthorised: !!_.intersection(
				useClasswizeConfigStore.getState().data.monitor_groups || [],
				useUserSessionStore.getState().getUsersGroups()
			).length,
		});
	};

	handleAction = (action, classroom) => {
		switch (action) {
			case TEACHER_ACTIONS.save:
				this.handleSubmitAddClassroom(classroom.sourceId);
				break;
			case TEACHER_ACTIONS.delete:
				this.handleSubmitDeleteClassroom(classroom);
				break;
			case TEACHER_ACTIONS.open:
				useStore.getState().classroom.setClassDetailsLoading();
				useUserSessionStore.getState().setActiveClassroom(classroom.sourceId);
				// eslint-disable-next-line no-case-declarations
				const classroomV2 = useStore.getState().classroom.getClassroomV2(classroom.id);
				if (this.coursesLicensed && !this.classroomLicensed) {
					this.props.router.navigate(`/classroom/${classroomV2.id}/courses`);
				} else {
					this.props.router.navigate(`/classroom/${classroomV2.id}`);
				}
				break;
			case TEACHER_ACTIONS.cancel:
				this.handleCancel();
				break;
			default:
				break;
		}
	};

	mapClassroomsToComponent = (classrooms) =>
		classrooms.map((c) => {
			const [isActive, expiry] = classroomActiveInfoV2(c);
			return <ClassroomTileV2 key={c.id} classroom={c} isActive={isActive} expiry={expiry} onClick={this.handleAction} />;
		});

	sortClassroomTiles = (previousClass, nextClass) => {
		const _previousClassName = `${
			previousClass.type.toLowerCase() === CLASSROOM_TYPES.GROUP ? previousClass.description : previousClass.name
		}${previousClass.sourceId}`;
		const _nextClassName = `${nextClass.type.toLowerCase() === CLASSROOM_TYPES.GROUP ? nextClass.description : nextClass.name}${
			nextClass.sourceId
		}`;
		return (previousClass?.personalisation?.displayName || _previousClassName).localeCompare(
			nextClass?.personalisation?.displayName || _nextClassName
		);
	};

	availableClassrooms = (showUnpinnedClasses) => {
		// sort classrooms by name in ascending order
		const pinnedClassrooms = useStore.getState().classroom.getPinnedClassrooms();
		const unpinnedClassrooms = useStore.getState().classroom.getUnpinnedClassrooms();

		if (pinnedClassrooms && pinnedClassrooms.length > 0) {
			const pinnedClassroomsSorted = pinnedClassrooms.sort(this.sortClassroomTiles);
			const unpinnedClassroomsSorted = unpinnedClassrooms.sort(this.sortClassroomTiles);
			return (
				<main>
					<SkipNavContent />
					<section>
						<Heading as="h2" fontSize={14} fontWeight={500} fontFamily="Roboto" p={[7, 7, 0, 7]}>
							PINNED CLASSES
						</Heading>
						<Box className="Classrooms_Pinned_Container" aria-label="PINNED CLASSES">
							{this.mapClassroomsToComponent(pinnedClassroomsSorted)}
						</Box>
					</section>
					<section>
						<Box mx={8} my={4}>
							<Accordion defaultIndex={[0]} allowMultiple aria-label="Other Classes">
								<AccordionItem border={"1px solid #fefefe"} rounded={"md"}>
									<AccordionButton
										background={"white"}
										_hover={{
											background: "#fafafa",
										}}
										px={5}
										py={3}
										roundedTop={"md"}
									>
										<Box color={"#333"} mr={"auto"}>
											OTHER CLASSES
										</Box>
										<AccordionIcon
											__css={{
												boxSize: "24px !important",
											}}
											color={"#333"}
										/>
									</AccordionButton>
									<AccordionPanel pb={4}>
										{showUnpinnedClasses && (
											<Box className="Classrooms_Unpinned_Container">{this.mapClassroomsToComponent(unpinnedClassroomsSorted)}</Box>
										)}
									</AccordionPanel>
								</AccordionItem>
							</Accordion>
						</Box>
					</section>
				</main>
			);
		}

		const classroomsSorted = useStore.getState().classroom.getClassrooms().sort(this.sortClassroomTiles);
		return <div className="Classrooms_container">{this.mapClassroomsToComponent(classroomsSorted)}</div>;
	};

	renderSync = () => {
		if (useStore.getState().classroom.teacherClassroomsSyncEnabled) {
			return (
				<Button
					title="Sync google classes"
					variant="unstyled"
					className="link sync_link track-Sync"
					onClick={() => this.setState({ openGoogleSyncModal: true })}
				>
					Sync
				</Button>
			);
		}
	};

	renderCtxbarLeft = () => {
		if (!useStore.getState().classroom.teacherClassroomsSyncEnabled) {
			return (
				<header className="ctxbar-my-classrooms-text">
					<h1 className="myclasses-heading">My classes</h1>
					{this.renderSync()}
				</header>
			);
		} else if (this.state.googleSyncStatus.status === "finished") {
			const syncDate = moment.unix(this.state.googleSyncStatus.start_time);

			let output;
			if (moment().isSame(syncDate, "day")) {
				// last sync was today
				output = syncDate.format("[today] h:mma");
			} else {
				// last sync wasn't today
				output = syncDate.format("dddd Do MMM");
			}

			return (
				<header className="ctxbar-my-classrooms-text">
					<h1 className="myclasses-heading">My classes</h1>
					<span className="Classrooms_googleSyncStatus--finished">Last sync {output}</span>
					{this.renderSync()}
				</header>
			);
		} else if (this.state.googleSyncStatus.status === "running") {
			return (
				<header className="ctxbar-my-classrooms-text">
					<h1 className="myclasses-heading">My classes</h1>
					<span className="Classrooms_googleSyncStatus--inProgress">
						<Spinner color="white" width={"12px"} height={"12px"} />
						Google Classroom sync
					</span>
					{this.renderSync()}
				</header>
			);
		} else if (this.state.googleSyncStatus.status === "error") {
			return (
				<React.Fragment>
					<header className="ctxbar-my-classrooms-text">
						<h1 className="myclasses-heading">My classes</h1>
					</header>
					<div
						className="button_small Classrooms_googleSyncStatus--error"
						onClick={() => this.setState({ openGoogleSyncErrorModal: true })}
					>
						<span>
							<ChakraFAIcon icon="fa-exclamation-triangle" variant="solid" />
							Sync Error
						</span>
					</div>
					{this.renderSync()}
				</React.Fragment>
			);
		} else {
			return (
				<header className="ctxbar-my-classrooms-text">
					<h1 className="myclasses-heading">My classes</h1>
					{this.renderSync()}
				</header>
			);
		}
	};

	handleCloseGoogleSyncModal = (force) => {
		if (!force && this.state.googleSyncStatus.status === "running") {
			return;
		}

		this.setState({ openGoogleSyncModal: false });
	};

	handleCloseGoogleSyncErrorModal = () => {
		this.setState({ openGoogleSyncErrorModal: false });
	};

	handleCloseGoogleSyncSuccessModal = () => {
		this.setState({ openGoogleSyncSuccessModal: false });
	};

	makeGoogleSyncStats = (syncStatus) => {
		const stats = {};

		const startDate = moment.unix(syncStatus.start_time);
		const endDate = moment.unix(syncStatus.end_time);
		const syncDuration = moment.duration(endDate.diff(startDate));

		const dateTimeFormatter = new Intl.DateTimeFormat([], {
			timeStyle: "short",
			dateStyle: "short",
		});
		stats.started = dateTimeFormatter.format(startDate.toDate());
		stats.duration = `${syncDuration.minutes()}m ${syncDuration.seconds()}s`;

		if (syncStatus.status === "finished") {
			stats.status = "Success";
			stats.classroomsMatched = syncStatus.classrooms_matched;
			stats.studentsMatched = syncStatus.students_matched;
		} else if (syncStatus.status === "error") {
			stats.status = "Failed";
			stats.errorMessage = syncStatus.error_message;
		}

		return stats;
	};

	renderGoogleSyncStats = (stats, label) => {
		if (!stats || Object.keys(stats).length === 0) {
			return null;
		}

		return (
			<React.Fragment>
				<br />
				<details>
					<summary>{label}</summary>
					<div className="showable-info">
						<ul>
							<li>Status: {stats.status}</li>
							<li>Started: {stats.started}</li>
							<li>Duration: {stats.duration}</li>
							{stats.status === "Success" && (
								<React.Fragment>
									<li>Classrooms matched: {stats.classroomsMatched}</li>
									<li>Students matched: {stats.studentsMatched}</li>
								</React.Fragment>
							)}
							{stats.status === "Failed" && stats.error_message && (
								<React.Fragment>
									<li>Error: {stats.error_message}</li>
								</React.Fragment>
							)}
						</ul>
					</div>
				</details>
			</React.Fragment>
		);
	};

	handleSubmitAddClassroom = async (classroomName, code, codeExpiry, isMonitoringClass, isPinned) => {
		return await useStore.getState().classroom.handleAddClassroom({
			name: classroomName,
			inviteCode: code,
			inviteCodeExpiry: codeExpiry,
			isMonitoringClass,
			pinned: isPinned,
		});
	};

	handleSubmitDeleteClassroom = (classroom) => {
		useStore.getState().classroom.handleDeleteClassroom(classroom.stableIdV1);
	};

	handleCancel = () => {};

	runGoogleSync = (force) => {
		for (const c of useStore.getState().classroom.getClassrooms()) {
			const [isActive] = classroomActiveInfoV2(c);
			if (isActive) {
				const errorMessage = "Sync can't be run while there are active classes. Please stop all active classes to start a sync.";
				this.setState({
					googleSyncError: errorMessage,
					openGoogleSyncErrorModal: true,
					openGoogleSyncModal: false,
				});
				return;
			}
		}

		if (!force && this.state.googleSyncStatus.status === "running") {
			return;
		}
		this.setState({
			googleSyncStatus: { status: "running" },
			isGoogleSyncModalCloseable: false,
			googleSyncInitiated: true,
		});

		this.closeGoogleSyncModalTimeout = setTimeout(() => {
			this.setState({
				openGoogleSyncModal: false,
				isGoogleSyncModalCloseable: true,
			});
		}, 300000); // 5 minutes

		Api.put(
			"/backend/sync/google",
			{},
			() => {
				this.pollGoogleSyncStatus();
			},
			(errorData) => {
				clearTimeout(this.closeGoogleSyncModalTimeout);
				const defaultErrorMessage =
					"Sync failed to start, please try again in a few minutes. If problems persist, please contact your administrator.";
				this.setState({
					googleSyncStatus: { status: "error" },
					googleSyncError: errorData.error_message || defaultErrorMessage,
					openGoogleSyncErrorModal: true,
					openGoogleSyncModal: false,
				});
			}
		);
	};

	pollGoogleSyncStatus = () => {
		this.googleSyncStateInterval = setInterval(() => {
			Api.get(
				"/backend/sync/google/status",
				(data) => {
					if (data.status.status === "error") {
						this.setState({
							googleSyncStatus: data.status,
							googleSyncError: data.status.error_message,
							googleSyncStats: this.makeGoogleSyncStats(data.status),
						});
						if (this.state.googleSyncInitiated) {
							this.setState({
								openGoogleSyncErrorModal: true,
								openGoogleSyncModal: false,
							});
						}
						clearInterval(this.googleSyncStateInterval);
						clearTimeout(this.closeGoogleSyncModalTimeout);
					} else if (data.status.status === "finished") {
						// we might need to think if we need to refetch all of this
						useStore.getState().classroom.fetchClassrooms();

						this.setState({
							googleSyncStatus: data.status,
							openGoogleSyncModal: false,
							googleSyncStats: this.makeGoogleSyncStats(data.status),
						});
						if (this.state.googleSyncInitiated) {
							this.setState({
								openGoogleSyncSuccessModal: true,
								googleSyncInitiated: false,
							});
						}
						clearInterval(this.googleSyncStateInterval);
						clearTimeout(this.closeGoogleSyncModalTimeout);
					}
				},
				() => {}
			);
		}, 30000); // 30 seconds
	};

	handleEditClassroomModalClose = () => {
		this.setState({
			showEditClassModal: false,
		});
	};

	render() {
		if (this.state.initialLoadComplete) {
			return (
				<div>
					<Nav />
					<div className="ctxbar ctxbar--class-selection">
						{this.renderCtxbarLeft()}
						<CtxBarRight
							teacherClassroomsEnabled={useStore.getState().classroom.teacherClassroomsEnabled}
							isMonitoringAuthorised={this.state.isMonitoringAuthorised}
						/>
					</div>
					{this.availableClassrooms(this.state.showUnpinnedClasses)}
					<ClassroomStartSession
						open={this.state.showStartClassModal}
						onStartError={this.state.startClassSessionError}
						isMonitoringClass={this.state.startClassSessionIsMonitoring}
					/>
					<ClassroomEditSession
						open={this.state.showEditClassModal}
						onExtendError={this.state.startClassSessionError}
						durationPlaceholder={this.state.classExpiryStr}
					/>
					<GoogleSyncModal
						open={this.state.openGoogleSyncModal}
						closeable={this.state.isGoogleSyncModalCloseable}
						close={this.handleCloseGoogleSyncModal}
						isSyncRunning={this.state.googleSyncStatus.status === "running"}
						syncStartUnix={this.state.googleSyncStatus.start_time}
						syncStats={this.state.googleSyncStats}
						renderSyncStats={this.renderGoogleSyncStats}
						runSync={this.runGoogleSync}
					/>
					<GoogleSyncErrorModal
						open={this.state.openGoogleSyncErrorModal}
						close={this.handleCloseGoogleSyncErrorModal}
						error={this.state.googleSyncError}
					/>
					<GoogleSyncSuccessModal
						open={this.state.openGoogleSyncSuccessModal}
						close={this.handleCloseGoogleSyncSuccessModal}
						syncStats={this.state.googleSyncStats}
						renderSyncStats={this.renderGoogleSyncStats}
					/>
				</div>
			);
		} else {
			return <FullPageLoader message="We're loading your classes as fast as we can. This may take up to a minute." />;
		}
	}
}

Classrooms.propTypes = {
	router: PropTypes.object.isRequired,
};

export default withRouter(Classrooms);

export const TEACHER_ACTIONS = {
	save: "save",
	cancel: "cancel",
	delete: "delete",
	open: "open",
};
